import {
  addStop,
  getPlan,
  getSights,
  NotFound,
  reorderStops,
  sightKinds,
  type Plan,
  type RoadSight,
  type RoadSights as Found,
  type SightKind,
} from '@trippath/shared';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { MapPanel } from '../../components/map/MapPanel';
import { day as dayLong } from '../../lib/format';
import type { MapData } from '../../lib/map';
import { useReadOnly } from '../../app/readOnly';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan; found: Found };

/** The detours a sight may cost, in minutes: there and back from the road. */
const DETOURS = [10, 30, 60] as const;

/** Each kind of sight's colour, for its picture's place. */
const TINTS: Record<SightKind, string> = {
  view: 'linear-gradient(135deg, #c48bd8, #6e2f8a)',
  nature: 'linear-gradient(135deg, #a8d08b, #4f7a2f)',
  town: 'linear-gradient(135deg, #d8c48b, #8a6f2f)',
  food: 'linear-gradient(135deg, #e8b48b, #a8562f)',
  fuel: 'linear-gradient(135deg, #8bb8d8, #2f5f8a)',
};

/** How far along a road a point is: the index of the road's point nearest it. */
function along(path: [number, number][], sight: RoadSight): number {
  let best = 0;
  let nearest = Number.POSITIVE_INFINITY;
  path.forEach(([lon, lat], at) => {
    const far = (lon - sight.lon) ** 2 + (lat - sight.lat) ** 2;
    if (far < nearest) {
      nearest = far;
      best = at;
    }
  });
  return best;
}

/** `5 h 10 min`, `45 min`. */
function hours(minutes: number, t: (key: string, values: Record<string, number>) => string) {
  const whole = Math.floor(minutes / 60);
  return whole > 0
    ? t('sights.hours', { hours: whole, minutes: minutes % 60 })
    : t('sights.minutes', { minutes });
}

/**
 * The sights along a stop's way on by road (the `RoadSights` mockup): the road and its sights on
 * the map; beside it, the way, the detour one will make and the kinds wanted; each sight with how
 * important open data says it is, where that is said, and its detour there and back; how
 * importance is found; and the sights picked added as stops of the way, in their order on it.
 */
export function RoadSights() {
  const { t, i18n } = useTranslation();
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const navigate = useNavigate();
  const { id = '', stop = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [detour, setDetour] = useState<number>(60);
  const [kinds, setKinds] = useState<SightKind[]>(['view', 'nature', 'town']);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([getPlan(id), getSights(id, stop, lang)]).then(
      ([plan, found]) => {
        if (live) setState({ kind: 'ready', plan, found });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, stop, lang]);

  const shown = useMemo(
    () =>
      state.kind === 'ready'
        ? state.found.sights.filter(
            (sight) =>
              kinds.includes(sight.kind) &&
              sight.detourMinutes !== null &&
              sight.detourMinutes <= detour,
          )
        : [],
    [state, kinds, detour],
  );
  const data = useMemo((): MapData => {
    const way = state.kind === 'ready' ? state.found.way : null;
    if (!way) return { points: [], lines: [] };
    const [first] = way.path;
    const last = way.path.at(-1);
    const ends = [
      ...(first ? [{ lon: first[0], lat: first[1], label: way.from }] : []),
      ...(last ? [{ lon: last[0], lat: last[1], label: way.to }] : []),
    ];
    return {
      points: [
        ...ends.map((end) => ({ ...end, kind: 'stop' as const })),
        ...shown.map((sight) => ({
          lat: sight.lat,
          lon: sight.lon,
          label: sight.name,
          kind: 'stay' as const,
          key: sight.id,
        })),
      ],
      lines: [{ kind: 'car', path: way.path }],
    };
  }, [state, shown]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { plan, found } = state;
  const { way } = found;
  const back = `/trips/${id}?stop=${stop}`;

  const add = (road: NonNullable<Found['way']>) => {
    // In their order along the road; each a stop of no nights.
    const chosen = found.sights
      .filter((sight) => picked.includes(sight.id))
      .sort((a, b) => along(road.path, a) - along(road.path, b));
    setBusy(true);
    setFailed(false);
    const zero = { min: 0, max: 0 };
    chosen
      .reduce<Promise<Plan>>(
        (was, sight) =>
          was.then(() =>
            addStop(id, {
              city: sight.name,
              country: road.country,
              days: 0,
              accommodation: zero,
              food: zero,
              local: zero,
              activities: zero,
              out: { mode: road.mode ?? 'car', estimate: zero, link: null },
              unresolved: false,
              place: { lat: sight.lat, lon: sight.lon, name: sight.name },
            }),
          ),
        Promise.resolve(plan),
      )
      .then((next) => {
        const made = next.stops.filter((one) => !plan.stops.some((was) => was.id === one.id));
        const order = plan.stops.flatMap((one) =>
          one.id === stop ? [one.id, ...made.map((m) => m.id)] : [one.id],
        );
        return reorderStops(id, order);
      })
      .then(
        () => {
          void navigate(back);
        },
        () => {
          setBusy(false);
          setFailed(true);
        },
      );
  };

  return (
    <div className="sights-page">
      <div className="sights-map">
        {way && (
          <MapPanel
            data={data}
            label={t('sights.map')}
            // The road and what is shown beside it, near enough to tell them apart.
            fit={data.points}
            closest={14}
            thin
          />
        )}
      </div>
      <aside className="sights-side" aria-label={t('sights.title')}>
        <div className="sights-sec">
          <Link className="back" to={back}>
            {t('sights.back')}
          </Link>
          <h1 className="visually-hidden">{t('sights.title')}</h1>
          {way ? (
            <>
              <p className="sights-way">
                {t('sights.way', {
                  from: way.from,
                  to: way.to,
                  how: t(`sights.by.${way.mode ?? 'car'}`),
                })}
              </p>
              <p className="small quiet">
                {[
                  way.road,
                  t('sights.km', { km: way.km }),
                  hours(way.minutes, t),
                  way.day && dayLong(way.day, lang),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <div className="sights-filters" role="group" aria-label={t('sights.detour')}>
                {DETOURS.map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    className="chip-button"
                    aria-pressed={detour === minutes}
                    onClick={() => {
                      setDetour(minutes);
                    }}
                  >
                    {t(`sights.upTo${String(minutes)}`)}
                  </button>
                ))}
              </div>
              <div className="sights-filters" role="group" aria-label={t('sights.kind')}>
                {sightKinds.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    className="chip-button"
                    aria-pressed={kinds.includes(kind)}
                    onClick={() => {
                      setKinds((was) =>
                        was.includes(kind) ? was.filter((one) => one !== kind) : [...was, kind],
                      );
                    }}
                  >
                    {t(`sights.kinds.${kind}`)}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="quiet">
              {t(found.status === 'failed' ? 'sights.failed' : 'sights.noRoad')}
            </p>
          )}
          {way && found.status === 'failed' && (
            <p className="form-failed" role="alert">
              {t('sights.failed')}
            </p>
          )}
        </div>
        {way && shown.length === 0 && found.status === 'ok' && (
          <p className="sights-sec quiet">{t('sights.none')}</p>
        )}
        {shown.length > 0 && (
          <ul className="sights-list">
            {shown.map((sight) => (
              <li key={sight.id} className="sight">
                <label className="sight-pick">
                  <input
                    type="checkbox"
                    checked={picked.includes(sight.id)}
                    aria-label={t('sights.pick', { name: sight.name })}
                    onChange={() => {
                      setPicked((was) =>
                        was.includes(sight.id)
                          ? was.filter((one) => one !== sight.id)
                          : [...was, sight.id],
                      );
                    }}
                  />
                  <span className="sight-pic" style={{ background: TINTS[sight.kind] }} />
                </label>
                <span>
                  <b>{sight.name}</b>
                  <small>
                    {[sight.about, t(`sights.source.${sight.source}`)]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                  <span
                    className="sight-score"
                    role="img"
                    aria-label={t('sights.score', { n: sight.score })}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <i key={n} className={n <= sight.score ? 'sight-full' : undefined} />
                    ))}
                  </span>
                </span>
                <span className="sight-km">
                  {sight.onWay
                    ? t('sights.onWay')
                    : t('sights.plus', { minutes: sight.detourMinutes })}
                  <small>{t('sights.km', { km: sight.offKm })}</small>
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="sights-sec" aria-labelledby={`${ids}-how`}>
          <span className="caps" id={`${ids}-how`}>
            {t('sights.howTitle')}
          </span>
          <span className="small">{t('sights.howLead')}</span>
          <ol className="small sights-how">
            <li>{t('sights.how1')}</li>
            <li>{t('sights.how2')}</li>
            <li>{t('sights.how3')}</li>
            <li>{t('sights.how4')}</li>
          </ol>
          <span className="small quiet mono">{t('sights.detourNote')}</span>
          {failed && (
            <p className="form-failed" role="alert">
              {t('sights.addFailed')}
            </p>
          )}
          {/* A trip read only: the sights on the way said, none added to it. */}
          {way && !locked && (
            <button
              type="button"
              className="button button-primary button-small"
              disabled={picked.length === 0 || busy}
              onClick={() => {
                add(way);
              }}
            >
              {t('sights.add')}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}
